import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
}

interface ToastContextType {
  showToast: (type: ToastType, title: string, message?: string) => void;
  success: (title: string, message?: string) => void;
  error: (title: string, message?: string) => void;
  warning: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const toastConfig = {
  success: {
    icon: CheckCircle,
    gradient: 'from-primary-500 to-primary-600',
    glowColor: 'shadow-primary-500/25',
    iconBg: 'bg-primary-500',
    titleColor: 'text-gray-900',
    messageColor: 'text-gray-600',
    progressColor: 'bg-gradient-to-r from-primary-400 to-primary-600',
    ringColor: 'ring-primary-500/20',
  },
  error: {
    icon: XCircle,
    gradient: 'from-secondary-500 to-secondary-600',
    glowColor: 'shadow-secondary-500/25',
    iconBg: 'bg-secondary-500',
    titleColor: 'text-gray-900',
    messageColor: 'text-gray-600',
    progressColor: 'bg-gradient-to-r from-secondary-400 to-secondary-600',
    ringColor: 'ring-secondary-500/20',
  },
  warning: {
    icon: AlertCircle,
    gradient: 'from-amber-500 to-orange-500',
    glowColor: 'shadow-amber-500/25',
    iconBg: 'bg-amber-500',
    titleColor: 'text-gray-900',
    messageColor: 'text-gray-600',
    progressColor: 'bg-gradient-to-r from-amber-400 to-orange-500',
    ringColor: 'ring-amber-500/20',
  },
  info: {
    icon: Info,
    gradient: 'from-primary-500 to-primary-600',
    glowColor: 'shadow-primary-500/25',
    iconBg: 'bg-primary-500',
    titleColor: 'text-gray-900',
    messageColor: 'text-gray-600',
    progressColor: 'bg-gradient-to-r from-primary-400 to-primary-600',
    ringColor: 'ring-primary-500/20',
  },
};

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

function ToastItem({ toast, onRemove }: ToastItemProps) {
  const config = toastConfig[toast.type];
  const IconComponent = config.icon;

  return (
    <div
      className={`
        relative overflow-hidden
        min-w-[340px] max-w-[420px]
        bg-white/95 backdrop-blur-xl
        rounded-2xl
        shadow-2xl ${config.glowColor}
        ring-1 ${config.ringColor}
        animate-toast-in
        group
      `}
    >
      {/* Top gradient accent line */}
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${config.gradient}`} />

      {/* Content */}
      <div className="p-4 pt-5">
        <div className="flex items-start gap-4">
          {/* Icon with gradient background */}
          <div className={`
            shrink-0
            w-10 h-10 rounded-xl
            bg-gradient-to-br ${config.gradient}
            flex items-center justify-center
          `}>
            <IconComponent className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>

          {/* Text content */}
          <div className="flex-1 min-w-0 pt-0.5">
            <p className={`font-semibold text-[15px] ${config.titleColor} leading-tight`}>
              {toast.title}
            </p>
            {toast.message && (
              <p className={`text-sm mt-1 ${config.messageColor} leading-relaxed`}>
                {toast.message}
              </p>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={() => onRemove(toast.id)}
            className="
              shrink-0 -mt-1 -mr-1
              w-8 h-8 rounded-lg
              flex items-center justify-center
              text-gray-400 hover:text-gray-600
              hover:bg-gray-100
              transition-all duration-200
              opacity-0 group-hover:opacity-100
              outline-none focus:outline-none
            "
          >
            <X className="w-4 h-4" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-gray-100">
        <div
          className={`h-full ${config.progressColor} animate-toast-progress rounded-full`}
          style={{ transformOrigin: 'left' }}
        />
      </div>
    </div>
  );
}

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (type: ToastType, title: string, message?: string) => {
      const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newToast: Toast = { id, type, title, message };

      setToasts((prev) => [...prev, newToast]);

      // Auto remove after 4 seconds
      setTimeout(() => {
        removeToast(id);
      }, 4000);
    },
    [removeToast]
  );

  const success = useCallback(
    (title: string, message?: string) => showToast('success', title, message),
    [showToast]
  );

  const error = useCallback(
    (title: string, message?: string) => showToast('error', title, message),
    [showToast]
  );

  const warning = useCallback(
    (title: string, message?: string) => showToast('warning', title, message),
    [showToast]
  );

  const info = useCallback(
    (title: string, message?: string) => showToast('info', title, message),
    [showToast]
  );

  return (
    <ToastContext.Provider value={{ showToast, success, error, warning, info }}>
      {children}
      {/* Toast Container - Bottom Right */}
      <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-3">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
